import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
  Grid,
} from "@material-ui/core";
import axios from "axios";
import { appUrl, adobeClientID } from "../../../../../utils/config";
import CustomDialogTitle from "./CustomDialogTitle";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

const FileViewer = ({
  openFileView,
  handleFileViewClose,
  file,
  deliverablesData,
  sponsorOrgID,
  cdmoOrgID,
  sponsorFileDetails,
  cdmoFileDetails,
}) => {
  const [fileURL, setFileURL] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [latestVersion, setLatestVersion] = useState(false);
  //console.log("latestVersion", latestVersion);

  const dispatch = useDispatch();
  const profileDetails = useSelector((state) => state.user.userDetails || {});

  const handleClose = useCallback(() => {
    setFileURL(null);
    handleFileViewClose();
  }, [handleFileViewClose]);

  const handleSave = useCallback(
    (metaData, content, collabrixFileID) => {
      return new Promise((resolve, reject) => {
        const blob = new Blob([content], { type: "application/pdf" });
        const reader = new FileReader();

        reader.onloadend = function () {
          const base64PdfContent = reader.result.split(",")[1];
          const payload = {
            fileName: metaData.fileName,
            organizationName: "Tree Tops Life Sciences Inc",
            docType: "scientific_file",
            fileData: `data:application/pdf;base64,${base64PdfContent}`,
            data: JSON.stringify({
              collabrixFileID,
              sponsorOrgID,
              cdmoOrgID,
            }),
          };

          axios
            .post(
              `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDocuments`,
              payload,
              { headers: { "Content-Type": "application/json" } }
            )
            .then((response) => {
              if (response.data.code === "SUCCESS") {
                resolve({
                  code: window.AdobeDC.View.Enum.ApiResponseCode.SUCCESS,
                  data: { metaData: response.data.data.metaData },
                });
              } else {
                reject({
                  code: window.AdobeDC.View.Enum.ApiResponseCode.FAIL,
                  data: {},
                });
              }
            })
            .catch((error) => {
              console.error("Error saving PDF:", error);
              reject({
                code: window.AdobeDC.View.Enum.ApiResponseCode.FAIL,
                data: {},
              });
            });
        };
        reader.readAsDataURL(blob);
      });
    },
    [sponsorOrgID, cdmoOrgID]
  );

  useEffect(() => {
    if (file.collabrixFileID) {
      setLoading(true);
      setError("");
      let readAccessOnly = true;
      let relevantFileDetails = [];
      if (sponsorFileDetails || cdmoFileDetails) {
        // Determine if the file belongs to sponsorFileDetails or cdmoFileDetails
        const isSponsorFile = sponsorFileDetails
          ? sponsorFileDetails.some(
              (sponsorFile) =>
                sponsorFile.collabrixFileID === file.collabrixFileID
            )
          : null;
        if (isSponsorFile) {
          // If it's a sponsor file, compare only with sponsorFileDetails
          relevantFileDetails = sponsorFileDetails;
        } else {
          // If it's not a sponsor file, compare with cdmoFileDetails
          relevantFileDetails = cdmoFileDetails;
        }
        // Find the max version number from the relevant file details
        const maxVersion = relevantFileDetails.reduce((max, fileDetail) => {
          return Math.max(max, fileDetail.versionNumber);
        }, 0);
        console.log("maxVersion", maxVersion);
        // Check if the file has the latest version
        const matchingFile = relevantFileDetails.find(
          (detail) => detail.collabrixFileID === file.collabrixFileID
        );
        if (matchingFile && matchingFile.versionNumber === maxVersion) {
          //console.log("if");
          setLatestVersion(true);
          readAccessOnly = false;
        } else {
          //console.log("else");
          setLatestVersion(false);
        }
      }

      axios
        .post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/downloadWorkItemFile`,
          { collabrixFileID: file.collabrixFileID },
          {
            responseType: "blob", // Expect a binary response
          }
        )
        .then((res) => {
          // Create a Blob URL from the response data
          const blob = new Blob([res.data], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          setFileURL(url);
          setLoading(false);

          // Extract the filename from the Content-Disposition header
          const contentDisposition = res.headers["content-disposition"];
          let fileName = "file.pdf";
          if (
            contentDisposition &&
            contentDisposition.indexOf("filename=") !== -1
          ) {
            const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
            if (matches != null && matches[1]) {
              fileName = matches[1];
            }
          }

          // Initialize the Adobe viewer here instead of calling the separate function
          if (window.AdobeDC) {
            const adobeDCView = new window.AdobeDC.View({
              clientId: adobeClientID,
              divId: "adobe-dc-view",
            });

            adobeDCView.registerCallback(
              window.AdobeDC.View.Enum.CallbackType.GET_USER_PROFILE_API,
              () => {
                return Promise.resolve({
                  code: window.AdobeDC.View.Enum.ApiResponseCode.SUCCESS,
                  data: {
                    userProfile: {
                      name: profileDetails.full_name,
                      email: profileDetails.email_address,
                    },
                  },
                });
              }
            );

            const saveOptions = {
              enableFocusPolling: true,
              showSaveButton: true,
            };

            adobeDCView.registerCallback(
              window.AdobeDC.View.Enum.CallbackType.SAVE_API,
              (metaData, content) =>
                handleSave(metaData, content, file.collabrixFileID),
              saveOptions
            );

            adobeDCView.previewFile(
              {
                content: { location: { url } },
                metaData: {
                  fileName: fileName,
                  id: "77c6fa5d-6d74-4104-8349-657c8411a834",
                  hasReadOnlyAccess: readAccessOnly,
                },
              },
              {
                showAnnotationTools: true,
                defaultViewMode: "TWO_COLUMN",
                enableAnnotationAPIs: true,
                includePDFAnnotations: true,
              }
            );
          }
        })
        .catch((err) => {
          console.error("Error downloading file:", err);
          setError("Something went wrong. Please try again.");
          setLoading(false);
        });
    }

    return () => {
      if (fileURL) {
        window.URL.revokeObjectURL(fileURL);
      }
    };
  }, [file.collabrixFileID]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://documentcloud.adobe.com/view-sdk/main.js";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script); // Cleanup on unmount
    };
  }, []); // This runs only once when the component mounts

  if (loading) {
    return (
      <Dialog open={openFileView} fullScreen>
        <CustomDialogTitle
          onBackClick={handleClose}
          title={deliverablesData.deliverableDescription[0]}
        />
        <DialogContent>
          <Grid container justifyContent="center" style={{ padding: "2rem" }}>
            <CircularProgress />
          </Grid>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={openFileView} fullScreen>
        <CustomDialogTitle
          onBackClick={handleClose}
          title={deliverablesData.deliverableDescription[0]}
        />
        <DialogContent>
          <Grid
            container
            justifyContent="center"
            style={{ padding: "1rem 0", display: "inherit" }}
          >
            <Grid
              item
              xs={12}
              style={{
                border: "1px solid #FF0000",
                backgroundColor: "#FFF0F0",
                opacity: "1",
                padding: "0.8rem",
                fontSize: "0.875rem",
                textAlign: "center",
              }}
            >
              <Typography variant="h6" style={{ fontSize: "0.875rem" }}>
                {error}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={openFileView} fullScreen>
      <CustomDialogTitle
        onBackClick={handleClose}
        title={deliverablesData.deliverableDescription[0]}
      />
      <DialogContent>
        <Grid container>
          <Grid item xs={12}>
            <div
              id="adobe-dc-view"
              style={{ height: "600px", width: "100%" }}
            ></div>
            <Grid
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography>Uploaded by {file.lastUpdatedBy}</Typography>
              <Typography>
                {moment(file.lastUpdatedDate).format("MMM D, YYYY, HH:mm")}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(FileViewer);
